"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { Type, Trash2, RotateCw, ChevronLeft, Plus, MousePointer, ChevronRight } from "lucide-react"
import { cn } from "../../../lib/utils"

// ─── Types ────────────────────────────────────────────────────────────────────

interface TextItem {
  index: number
  text: string
  fill: string
  fontFamily: string
  fontSize: number
}

interface FontOption {
  id: string
  name: string
  path: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function readWindowTexts(): TextItem[] {
  const arr: any[] = (window as any).arrayGroupTexts ?? []
  const items: TextItem[] = []
  arr.forEach((obj, i) => {
    if (!obj) return
    items.push({
      index: i,
      text: obj.text ?? "",
      fill: obj.fill ?? "#ffffff",
      fontFamily: obj.fontFamily ?? "",
      fontSize: obj.fontSize ?? 30,
    })
  })
  return items
}

function readAvailableFonts(): FontOption[] {
  const fonts: FontOption[] = []
  document.querySelectorAll(".font1").forEach((el: Element) => {
    const id = el.getAttribute("id") ?? ""
    const name = el.getAttribute("title") ?? ""
    const path = el.getAttribute("data-path") ?? ""
    if (name) fonts.push({ id, name, path })
  })
  return fonts
}

function resolveTableColors(): string[] {
  const w = window as any
  const allTables: any[] = w.colors ?? []
  const dataShared = w.dataShared
  if (!allTables.length) return []
  const mainId = dataShared?.mainColorTable
  if (mainId) {
    const table = allTables.find((t: any) => t.id === mainId)
    if (table?.colors?.length) return table.colors.map((c: any) => c.hex)
  }
  if (allTables[0]?.colors?.length) return allTables[0].colors.map((c: any) => c.hex)
  return []
}

// ─── Inline color table picker ────────────────────────────────────────────────

function ColorTablePicker({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (color: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [tableColors, setTableColors] = useState<string[]>(() => resolveTableColors())

  // Load on mount and whenever colors become available
  useEffect(() => {
    const load = () => {
      const colors = resolveTableColors()
      if (colors.length > 0) setTableColors(colors)
    }
    load()
    // Poll until window.colors is ready (loaded async in Loads.js)
    const interval = setInterval(() => {
      const colors = resolveTableColors()
      if (colors.length > 0) {
        setTableColors(colors)
        clearInterval(interval)
      }
    }, 300)
    window.addEventListener("svgColorsReady", load)
    return () => {
      clearInterval(interval)
      window.removeEventListener("svgColorsReady", load)
    }
  }, [])

  return (
    <div className="rounded-2xl bg-secondary/20 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</label>
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2 touch-manipulation"
        >
          <span className="text-xs text-muted-foreground">{open ? "fechar" : "alterar"}</span>
          <div
            className={cn(
              "w-6 h-6 rounded-lg border-2 transition-all",
              open ? "border-primary scale-110" : "border-border/40"
            )}
            style={{ backgroundColor: value }}
          />
        </button>
      </div>

      {open && (
        <div className="rounded-xl bg-secondary/30 p-2.5 max-h-48 overflow-y-auto">
          {tableColors.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {tableColors.map(color => {
                const selected = value === color
                const h = color.startsWith("#") ? color : "#000"
                const r = parseInt(h.slice(1,3),16), g = parseInt(h.slice(3,5),16), b = parseInt(h.slice(5,7),16)
                const cIsLight = (r*0.299+g*0.587+b*0.114) > 186
                return (
                  <button
                    key={color}
                    onClick={() => { onChange(color); setOpen(false) }}
                    title={color}
                    className={cn(
                      "w-8 h-8 rounded-xl border-2 transition-all hover:scale-110 active:scale-95 touch-manipulation flex items-center justify-center flex-shrink-0",
                      selected
                        ? "border-primary ring-2 ring-primary/40 scale-110 shadow-lg"
                        : cIsLight ? "border-border/60 hover:border-primary/50" : "border-white/10 hover:border-white/40"
                    )}
                    style={{ backgroundColor: color }}
                  >
                    {selected && (
                      <svg viewBox="0 0 12 12" className={cn("w-3 h-3", cIsLight ? "fill-gray-800" : "fill-white")}>
                        <path d="M1 6l3.5 3.5L11 2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                )
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-3">
              Carregando tabela de cores…
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Waiting for click ────────────────────────────────────────────────────────

function WaitingForClick({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-primary/60 bg-primary/5 p-4 flex flex-col items-center gap-3 text-center animate-in fade-in duration-200">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
        <MousePointer className="w-5 h-5 text-primary" />
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground">Clique no uniforme</p>
        <p className="text-xs text-muted-foreground mt-0.5">Toque onde quer posicionar o texto</p>
      </div>
      <button
        onClick={onCancel}
        className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
      >
        Cancelar
      </button>
    </div>
  )
}

// ─── Text card ────────────────────────────────────────────────────────────────

function TextCard({ item, onEdit }: { item: TextItem; onEdit: (item: TextItem) => void }) {
  return (
    <button
      onClick={() => onEdit(item)}
      className="group w-full flex items-center gap-3 px-3 py-3 rounded-2xl border-2 border-border/40 hover:border-primary/60 bg-secondary/20 transition-all duration-150 hover:bg-secondary/40 active:scale-95 touch-manipulation text-left"
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold border border-border/30"
        style={{ color: item.fill, fontFamily: item.fontFamily, backgroundColor: "rgba(0,0,0,0.3)" }}
      >
        T
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate"
          style={{ fontFamily: item.fontFamily, color: item.fill }}>
          {item.text || "—"}
        </p>
        <p className="text-xs text-muted-foreground truncate">{item.fontFamily || "Padrão"} · {item.fontSize}px</p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  )
}

// ─── Font picker panel ────────────────────────────────────────────────────────

function FontPickerPanel({
  currentFont,
  previewText,
  onSelect,
  onBack,
}: {
  currentFont: string
  previewText: string
  onSelect: (font: FontOption) => void
  onBack: () => void
}) {
  const [fonts, setFonts] = useState<FontOption[]>([])
  const display = previewText.trim() || "txt!"

  useEffect(() => {
    const load = () => {
      const f = readAvailableFonts()
      if (f.length > 0) setFonts(f)
    }
    load()
    const t = setTimeout(load, 500)
    return () => clearTimeout(t)
  }, [])

  return (
    <div className="space-y-3 animate-in slide-in-from-right-4 duration-200">
      <div className="flex items-center gap-2">
        <button onClick={onBack}
          className="w-8 h-8 rounded-xl bg-secondary/60 hover:bg-secondary active:scale-95 flex items-center justify-center transition-all touch-manipulation flex-shrink-0">
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <span className="text-sm font-semibold text-foreground">Escolher Fonte</span>
      </div>

      {/* Font grid — each cell shows the preview text in that font */}
      <div className="rounded-2xl bg-secondary/20 p-2.5 max-h-80 overflow-y-auto">
        <div className="grid grid-cols-2 gap-1.5">
          {fonts.length === 0 ? (
            <p className="col-span-2 text-xs text-muted-foreground text-center py-4">
              Nenhuma fonte disponível
            </p>
          ) : (
            fonts.map(font => (
              <button
                key={font.id}
                onClick={() => onSelect(font)}
                className={cn(
                  "flex flex-col items-center justify-center px-2 py-3 rounded-xl transition-all touch-manipulation border-2 gap-1",
                  currentFont === font.name
                    ? "bg-primary/20 border-primary/60"
                    : "bg-secondary/30 border-transparent hover:border-border/40 active:scale-95"
                )}
              >
                {/* Text in that font — the real preview */}
                <span
                  className="text-xl leading-tight text-foreground truncate w-full text-center"
                  style={{ fontFamily: font.name }}
                >
                  {display}
                </span>
                {/* Tiny font name below for reference */}
                <span className="text-[9px] text-muted-foreground/60 truncate w-full text-center leading-none">
                  {font.name}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Edit panel ───────────────────────────────────────────────────────────────

function TextEditPanel({
  item,
  onBack,
  onDeleted,
  onUpdated,
}: {
  item: TextItem
  onBack: () => void
  onDeleted: () => void
  onUpdated: (updated: Partial<TextItem>) => void
}) {
  const w = window as any
  const obj = w.arrayGroupTexts?.[item.index]

  const [text, setText] = useState(item.text)
  const [fill, setFill] = useState(item.fill)
  const [fontSize, setFontSize] = useState(item.fontSize)
  const [strokeWidth, setStrokeWidth] = useState<number>(obj?.strokeWidth ?? 0)
  const [stroke, setStroke] = useState<string>(obj?.stroke ?? "#000000")
  const [angle, setAngle] = useState(Math.round(obj?.angle ?? 0))
  const [fontFamily, setFontFamily] = useState(item.fontFamily)
  const [showFonts, setShowFonts] = useState(false)

  useEffect(() => {
    if (!obj) return
    w.targetText = obj
    w.targetImage = obj
    w.editingImage = true
    w["fabricRes"]?.("low")
  }, [obj])

  const update = (props: Record<string, any>) => {
    if (!obj) return
    obj.set(props)
    obj.setCoords()
    w.fabricCanvas?.requestRenderAll()
    setTimeout(() => w["updateCanvas"]?.(), 50)
  }

  const handleText = (v: string) => {
    setText(v)
    update({ text: v })
    onUpdated({ text: v })
  }

  const handleFill = (v: string) => {
    setFill(v)
    update({ fill: v })
    onUpdated({ fill: v })
  }

  const handleFontSize = (v: number) => {
    setFontSize(v)
    update({ fontSize: v })
    onUpdated({ fontSize: v })
  }

  const handleStrokeWidth = (v: number) => {
    setStrokeWidth(v)
    update({ strokeWidth: v })
  }

  const handleStroke = (v: string) => {
    setStroke(v)
    update({ stroke: v })
  }

  const handleAngle = (v: number) => {
    setAngle(v)
    w["rotate-text-iframe"]?.(v)
  }

  const handleFontSelect = (font: FontOption) => {
    setFontFamily(font.name)
    w.fontFamily = font.name
    update({ fontFamily: font.name })
    onUpdated({ fontFamily: font.name })
    setShowFonts(false)
  }

  const handleDelete = () => {
    if (!obj) return
    w.fabricCanvas?.remove(obj)
    w.fabricCanvas?.renderAll()
    w["updateCanvas"]?.()
    w.arrayGroupTexts[item.index] = null
    w.editingImage = false
    w.targetText = null
    w.targetImage = null
    w["fabricRes"]?.("high")
    window.dispatchEvent(new Event("textsChanged"))
    onDeleted()
  }

  const handleBack = () => {
    w.editingImage = false
    w.targetText = null
    w.targetImage = null
    w["fabricRes"]?.("high")
    w["setupCamera"]?.()
    w.cube && (w.cube.visible = false)
    onBack()
  }

  if (showFonts) {
    return (
      <FontPickerPanel
        currentFont={fontFamily}
        previewText={text}
        onSelect={handleFontSelect}
        onBack={() => setShowFonts(false)}
      />
    )
  }

  return (
    <div className="space-y-3 animate-in slide-in-from-right-4 duration-200">

      {/* Header */}
      <div className="flex items-center gap-2">
        <button onClick={handleBack}
          className="w-8 h-8 rounded-xl bg-secondary/60 hover:bg-secondary active:scale-95 flex items-center justify-center transition-all touch-manipulation flex-shrink-0">
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <span className="text-sm font-semibold text-foreground">Editar Texto</span>
        <div className="ml-auto px-2 py-1 rounded-lg bg-secondary/40 text-sm max-w-[120px] truncate"
          style={{ fontFamily, color: fill }}>
          {text || "—"}
        </div>
      </div>

      {/* Text content */}
      <div className="rounded-2xl bg-secondary/20 p-3 space-y-2">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Texto</label>
        <input
          type="text"
          value={text}
          onChange={e => handleText(e.target.value)}
          className="w-full bg-secondary/40 border border-border/40 rounded-xl px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/60 transition-colors"
          placeholder="Digite o texto..."
        />
      </div>

      {/* Colors */}
      <ColorTablePicker label="Cor do texto" value={fill} onChange={v => { handleFill(v) }} />
      <ColorTablePicker label="Cor da borda" value={stroke} onChange={v => { handleStroke(v) }} />

      {/* Font size + stroke width */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-2xl bg-secondary/20 p-3 space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tamanho</label>
            <span className="text-xs font-mono text-muted-foreground">{fontSize}px</span>
          </div>
          <input type="range" min={8} max={300} value={fontSize}
            onChange={e => handleFontSize(Number(e.target.value))}
            className="w-full h-2 rounded-full accent-primary cursor-pointer touch-manipulation" />
        </div>
        <div className="rounded-2xl bg-secondary/20 p-3 space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Espessura</label>
            <span className="text-xs font-mono text-muted-foreground">{strokeWidth}px</span>
          </div>
          <input type="range" min={0} max={20} value={strokeWidth}
            onChange={e => handleStrokeWidth(Number(e.target.value))}
            className="w-full h-2 rounded-full accent-primary cursor-pointer touch-manipulation" />
        </div>
      </div>

      {/* Rotation */}
      <div className="rounded-2xl bg-secondary/20 p-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RotateCw className="w-4 h-4 text-muted-foreground" />
            <label className="text-sm font-medium text-foreground">Rotação</label>
          </div>
          <span className="text-xs font-mono text-muted-foreground">{angle}°</span>
        </div>
        <input type="range" min={0} max={360} value={angle}
          onChange={e => handleAngle(Number(e.target.value))}
          className="w-full h-2 rounded-full accent-primary cursor-pointer touch-manipulation" />
      </div>

      {/* Font */}
      <button
        onClick={() => setShowFonts(true)}
        className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl bg-secondary/20 hover:bg-secondary/40 border border-border/30 hover:border-primary/40 transition-all touch-manipulation"
      >
        <Type className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-foreground">Fonte</p>
          <p className="text-xs text-muted-foreground truncate" style={{ fontFamily }}>
            {fontFamily || "Padrão"}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      </button>

      {/* Delete */}
      <button onClick={handleDelete}
        className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/40 transition-all touch-manipulation">
        <Trash2 className="w-4 h-4 text-red-400 flex-shrink-0" />
        <span className="text-sm font-medium text-red-400">Remover texto</span>
      </button>

    </div>
  )
}

// ─── Main TextPicker component ────────────────────────────────────────────────

export function TextPicker() {
  const [texts, setTexts] = useState<TextItem[]>([])
  const [editing, setEditing] = useState<TextItem | null>(null)
  const [waitingForClick, setWaitingForClick] = useState(false)
  const prevLengthRef = useRef(0)

  const refreshTexts = useCallback(() => {
    setTexts(readWindowTexts())
  }, [])

  useEffect(() => {
    refreshTexts()

    const onLayout = () => {
      setTexts([])
      setEditing(null)
      setWaitingForClick(false)
      prevLengthRef.current = 0
    }

    window.addEventListener("svgColorsReady", refreshTexts)
    window.addEventListener("layoutChanged", onLayout)
    window.addEventListener("textsChanged", refreshTexts)
    return () => {
      window.removeEventListener("svgColorsReady", refreshTexts)
      window.removeEventListener("layoutChanged", onLayout)
      window.removeEventListener("textsChanged", refreshTexts)
    }
  }, [refreshTexts])

  // Poll for new text placement after user clicks on 3D
  useEffect(() => {
    if (!waitingForClick) return

    const interval = setInterval(() => {
      const arr: any[] = (window as any).arrayGroupTexts ?? []
      const curr = arr.filter(Boolean).length
      if (curr > prevLengthRef.current) {
        prevLengthRef.current = curr
        setWaitingForClick(false)
        window.dispatchEvent(new Event("logoPlacementEnd"))
        refreshTexts()
      }
    }, 150)

    return () => clearInterval(interval)
  }, [waitingForClick, refreshTexts])

  const handleAdd = () => {
    const w = window as any
    const { fabric } = w
    if (!fabric) return

    const text = new fabric.Text("txt!", {
      fontSize: 30,
      fill: "#ffffff",
      ...(w.fontFamily ? { fontFamily: w.fontFamily } : {}),
    })
    text.new_id = Math.random()

    w.addingText = true
    w.currentText = text
    w.editingImage = true
    w.backText = true

    prevLengthRef.current = (w.arrayGroupTexts ?? []).filter(Boolean).length
    setWaitingForClick(true)
    window.dispatchEvent(new Event("logoPlacementStart")) // reuse same overlay event
    w['toast']?.("Clique na roupa onde vc quer posicionar o texto!", "info")
  }

  const handleCancelWaiting = () => {
    const w = window as any
    w.addingText = false
    w.currentText = null
    w.editingImage = false
    setWaitingForClick(false)
    window.dispatchEvent(new Event("logoPlacementEnd"))
  }

  const handleEdit = (item: TextItem) => {
    const w = window as any
    const obj = w.arrayGroupTexts?.[item.index]
    if (!obj) return
    w.targetText = obj
    w.targetImage = obj
    w.editingImage = true
    w["fabricRes"]?.("low")
    setEditing(item)
  }

  const handleDeleted = () => {
    setEditing(null)
    refreshTexts()
  }

  const handleUpdated = (updated: Partial<TextItem>) => {
    if (!editing) return
    setEditing(e => e ? { ...e, ...updated } : null)
    setTexts(prev => prev.map(t =>
      t.index === editing.index ? { ...t, ...updated } : t
    ))
  }

  return (
    <div className="space-y-5">

      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
          <Type className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Textos</h3>
          <p className="text-xs text-muted-foreground">
            {editing ? "Editando texto"
              : texts.length > 0 ? "Clique em um texto para editar"
              : "Adicione nome, número ou texto"}
          </p>
        </div>
      </div>

      {editing ? (
        <TextEditPanel
          item={editing}
          onBack={() => setEditing(null)}
          onDeleted={handleDeleted}
          onUpdated={handleUpdated}
        />
      ) : (
        <div className="space-y-3">

          {waitingForClick && <WaitingForClick onCancel={handleCancelWaiting} />}

          {texts.length > 0 && (
            <div className="space-y-2">
              {texts.map(item => (
                <TextCard key={item.index} item={item} onEdit={handleEdit} />
              ))}
            </div>
          )}

          {texts.length === 0 && !waitingForClick && (
            <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
              <div className="w-14 h-14 rounded-2xl bg-secondary/40 flex items-center justify-center">
                <Type className="w-7 h-7 text-muted-foreground/50" />
              </div>
              <p className="text-sm text-muted-foreground max-w-[180px]">
                Nenhum texto encontrado no uniforme
              </p>
            </div>
          )}

          {!waitingForClick && (
            <button
              onClick={handleAdd}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border-2 border-dashed border-border/50 hover:border-primary/60 bg-secondary/10 hover:bg-primary/5 transition-all touch-manipulation group"
            >
              <Plus className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              <span className="text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors">
                Adicionar texto
              </span>
            </button>
          )}

        </div>
      )}
    </div>
  )
}